import React from 'react';
import Button from 'Components/DesignSystem/Button/Button';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Typography from 'Components/DesignSystem/Typography/Typography';

type ConfirmDialogProps = {
    title: string;
    message: string;
    confirmText: string;
    cancelText: string;
    onClose: () => void;
    onConfirm: () => void;
}

/**
 * The control-model change confirmation (`okf/design-system/e2e.md` § 147).
 *
 * Another hand-rolled `tw-fixed tw-inset-0` overlay: no `role="dialog"`, no accessible
 * name, no focus trap, no Escape — and this one guards a genuinely destructive step
 * ("Selecting a different control model will stop {model}"), which is exactly the case
 * where a keyboard user must not be able to tab out behind the dialog and click
 * something else.
 *
 * The warning band was `tw-bg-[#fffae6]` inside `tw-border-[#ffc400]` with a
 * `YellowWarningIcon`. It became a DS `Alert tone="warning"` in § 147 and is plain
 * prose since § 166: `Dialog tone="warning"` already puts `AlertTriangle` beside the
 * title, so the alert drew a second identical triangle directly beneath it — and as
 * the dialog's only child its tint had no surroundings to mark itself out from.
 * The tone now speaks once, from the header.
 *
 * **The confirm button keeps its red.** `Dialog tone="warning"` sets the header glyph;
 * the action itself is `Button variant="danger"`, the destructive-outline grammar from
 * § 84, rather than the legacy `hover:tw-bg-red-500 tw-bg-red-600` fill — two stock
 * Tailwind reds, neither of them `tone-danger`.
 */
const ConfirmDialog = ({ title, message, confirmText, cancelText, onClose, onConfirm }: ConfirmDialogProps) => {
    return (
        <Dialog
            isOpen
            onClose={onClose}
            title={title}
            tone="warning"
            size="md"
            data-testid="ab-confirm-dialog"
            footer={
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-2">
                    <Button
                        variant="secondary"
                        onClick={onClose}
                        data-testid="ab-confirm-dialog-cancel-btn"
                    >
                        {cancelText}
                    </Button>
                    <Button
                        variant="danger"
                        onClick={onConfirm}
                        data-testid="detail-confirmdialog-button-1"
                    >
                        {confirmText}
                    </Button>
                </div>
            }
        >
            <Typography
                variant="body-primary"
                as="p"
                className="tw-text-ink-secondary"
                data-testid="ab-confirm-dialog-message"
            >
                {message}
            </Typography>
        </Dialog>
    );
}

export default ConfirmDialog;
